import React, { useState, useEffect } from 'react';
import { EmailGenerator } from '../components/EmailBox/EmailGenerator';
import { EmailList } from '../components/EmailBox/EmailList';
import { useTranslation } from 'react-i18next';
import { InformationSections } from '../components/sections/InformationSections';
import { useParams } from 'react-router-dom';

export const HomePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || 'en';  // 如果没有语言参数，默认使用英文

  const [currentEmail, setCurrentEmail] = useState(() => {
    return localStorage.getItem('currentEmail') || '';
  });

  useEffect(() => {
    i18n.changeLanguage(currentLang);
  }, [lang]);

  // 添加结构化数据
  React.useEffect(() => {
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "Secure Anonymous TempMail ",
      "url": "https://tempmail.love",
      "applicationCategory": "UtilitiesApplication",
      "description": "Create disposable temporary email addresses instantly. Protect your privacy with our secure, free, and easy-to-use temporary email service.",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "ratingCount": "1250"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  // 预加载关键资源
  React.useEffect(() => {
    const preloadResources = [
      '/android-chrome-192x192.png',
      '/og-image.jpg',
      // Add other critical resources here
    ];

    preloadResources.forEach(resource => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = resource.endsWith('.jpg') ? 'image' : 'image';
      link.href = resource;
      document.head.appendChild(link);
    });

    return () => {
      document.querySelectorAll('link[rel="preload"]').forEach(el => el.remove());
    };
  }, []);

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-white to-green-50">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" aria-hidden="true">
          <div className="absolute top-20 left-10 w-64 h-64 bg-primary-light/5 rounded-full blur-3xl" />
          <div className="absolute top-40 right-20 w-72 h-72 bg-primary-dark/5 rounded-full blur-3xl" />
        </div>

        {/* Main content */}
        <main className="relative py-4 sm:py-6">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
            {/* Hero Section */}
            <header className="text-center">
              <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-primary-light to-primary-dark bg-clip-text text-transparent">
                {t('app.title')}
              </h1>
              <p className="text-lg text-gray-600">
                {t('app.subtitle')}
              </p>
            </header>

            {/* Email Generator Section */}
            <section aria-label="Email Generator" className="bg-white rounded-xl sm:rounded-2xl shadow-lg mb-6 sm:mb-8">
              <div className="p-3 sm:p-4">
                <EmailGenerator onEmailGenerated={setCurrentEmail} />
              </div>
              
              {currentEmail && (
                <div className="px-3 sm:px-4 pb-3 sm:pb-4">
                  <EmailList emailAddress={currentEmail} />
                </div>
              )}
            </section>

            {/* Information Content Section */}
            <InformationSections />
          </div>
        </main>

        {/* Add breadcrumb for SEO */}
        <nav aria-label="Breadcrumb" className="hidden">
          <ol itemScope itemType="https://schema.org/BreadcrumbList">
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <a itemProp="item" href="https://tempmail.love">
                <span itemProp="name">Home</span>
              </a>
              <meta itemProp="position" content="1" />
            </li>
          </ol>
        </nav>
      </div>
    </>
  );
}; 